import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Image, Loader2, Calendar, DollarSign, Trophy, Award, Mail, Phone } from 'lucide-react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { useToast } from '../../../components/ui/use-toast';
import { supabase } from '../../../lib/supabase';
import { PublicHeader } from '../../../components/layout/PublicHeader';
import { PublicFooter } from '../../../components/layout/PublicFooter';
import { motion } from 'framer-motion';
import { Checkbox } from '../../../components/ui/checkbox';
import { SEOMetaTags } from '../../../components/shared/SEOMetaTags';
import { TopFiveGlobal } from '../../../components/shared/TopFiveGlobal'; // Importer le composant

// Service pour la soumission de concours
const submitContestProposal = async (data: {
  name: string;
  description: string;
  start_date: string; 
  end_date: string;
  photo_url: string;
  logo_url: string;
  contact_email: string;
  contact_phone: string;
  submitter_info: any;
}) => {
  try {
    console.log('Données à insérer:', data);
    
    const { error } = await supabase
      .from('public_contests_submissions')
      .insert([{
        ...data,
        created_at: new Date().toISOString(),
        status: 'pending'
      }]);

    if (error) {
      console.error('Erreur d\'insertion:', error);
      throw error;
    }
    
    console.log('Soumission réussie');
    return { success: true };
  } catch (error) {
    console.error('Erreur:', error);
    return { success: false, error };
  }
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const contestSubmissionSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  description: z.string().min(1, 'La description est requise'),
  start_date: z.string().min(1, 'La date de début est requise'),
  end_date: z.string().min(1, 'La date de fin est requise'),
  contact_email: z.string().email('Email invalide'),
  contact_phone: z.string().min(1, 'Le numéro de téléphone est requis'),
  submitter_name: z.string().min(1, 'Votre nom est requis'),
  submitter_organization: z.string().optional(),
  cover: z
    .instanceof(FileList)
    .refine((files) => files.length > 0, 'Une image de couverture est requise')
    .refine((files) => files[0].size <= MAX_FILE_SIZE, 'La taille maximale du fichier est de 5MB')
    .refine(
      (files) => ACCEPTED_IMAGE_TYPES.includes(files[0].type),
      'Format accepté : .jpg, .jpeg, .png, .webp'
    ),
  logo: z
    .instanceof(FileList)
    .refine((files) => files.length > 0, 'Un logo est requis')
    .refine((files) => files[0].size <= MAX_FILE_SIZE, 'La taille maximale du fichier est de 5MB')
    .refine(
      (files) => ACCEPTED_IMAGE_TYPES.includes(files[0].type),
      'Format accepté : .jpg, .jpeg, .png, .webp'
    ),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: 'Vous devez accepter les conditions de service',
  }),
});

type ContestSubmissionFormValues = z.infer<typeof contestSubmissionSchema>;

const ContestSubmission = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const form = useForm<ContestSubmissionFormValues>({
    resolver: zodResolver(contestSubmissionSchema),
    defaultValues: {
      name: '',
      description: '',
      start_date: `${new Date().toISOString().split('T')[0]}T${new Date().toTimeString().slice(0, 5)}`,
      end_date: `${new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}T${new Date().toTimeString().slice(0, 5)}`,
      contact_email: '',
      contact_phone: '',
      submitter_name: '',
      submitter_organization: '',
      acceptTerms: false,
    },
  });

  const convertFileToBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setCoverPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const onSubmit = async (data: ContestSubmissionFormValues) => {
    try {
      setSubmitting(true);
      
      // Convertir les images en base64
      const coverBase64 = await convertFileToBase64(data.cover[0]);
      const logoBase64 = await convertFileToBase64(data.logo[0]);

      // Préparer les informations du soumissionnaire
      const submitterInfo = {
        name: data.submitter_name,
        organization: data.submitter_organization || null
      };

      // Soumettre la proposition de concours
      const result = await submitContestProposal({
        name: data.name,
        description: data.description,
        start_date: new Date(data.start_date).toISOString(),
        end_date: new Date(data.end_date).toISOString(),
        photo_url: coverBase64,
        logo_url: logoBase64,
        contact_email: data.contact_email,
        contact_phone: data.contact_phone,
        submitter_info: submitterInfo
      });

      if (!result.success) throw result.error;

      // Réinitialiser le formulaire et afficher un message de succès
      form.reset();
      setCoverPreview(null);
      setLogoPreview(null);
      setSuccess(true);
      
      toast({
        title: 'Soumission réussie',
        description: 'Votre proposition de concours a été soumise avec succès et sera examinée par notre équipe.',
      });
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la soumission. Veuillez réessayer.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOMetaTags
        title="Soumission de concours | ERNEST NZOGOUG"
        description="Soumettez votre proposition de concours sur ERNEST NZOGOUG. Participez et tentez de gagner de nombreux prix !"
        image="https://nzofoug.com/contest-default.jpg"
        url="https://nzofoug.com/soumission"
      />
      <PublicHeader />
      
      <div className="bg-[#111216] text-white relative overflow-hidden pt-20">
        {/* Background Image avec Overlay et Parallaxe */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: 'url(https://images.rawpixel.com/image_800/cHJpdmF0ZS9sci9pbWFnZXMvd2Vic2l0ZS8yMDI0LTA4L3N0YXJ0dXBpbWFnZXNfZ29sZF9nbGl0dGVyX2dvbGRfY29uZmV0dGlfYm9rZWhfc3BhcmtsZV9zaW1wbGVfZ185MTEzYzJkMy03YjEyLTQwYmQtYjNhYS04OTVlOGU4MmM5N2NfMi5qcGc.jpg)',
            transform: 'translateZ(0)',
            willChange: 'transform',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/80 to-black/95 backdrop-blur-[2px]"></div>
        </div>

        {/* Hero Section */}
        <div className="relative min-h-[40vh] overflow-hidden flex items-center">
          {/* Contenu du Hero */}
          <div className="relative z-20 container mx-auto px-4">
            <motion.div 
              className="max-w-4xl mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block p-2 bg-yellow-400/10 rounded-xl mb-4">
                <Trophy className="h-10 w-10 text-yellow-400" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-200 to-yellow-400 text-transparent bg-clip-text">
                Proposer un concours
              </h1>
              <p className="text-lg text-gray-300 mb-6 max-w-2xl mx-auto">
                Soumettez votre idée de concours en remplissant le formulaire ci-dessous. Notre équipe examinera votre proposition et vous contactera sous peu.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="relative z-10 container mx-auto px-4 py-12">
          <motion.div 
            className="max-w-3xl mx-auto bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-white/10 shadow-xl"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {success ? (
              <motion.div 
                className="text-center py-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="inline-block p-4 bg-green-500/20 rounded-full mb-6">
                  <Award className="h-16 w-16 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold mb-4 text-green-400">Proposition soumise avec succès !</h2>
                <p className="text-gray-300 mb-8">
                  Votre proposition de concours a été enregistrée et sera examinée par notre équipe. Nous vous contacterons prochainement pour la suite du processus.
                </p>
                <Button 
                  onClick={() => setSuccess(false)} 
                  className="bg-gradient-to-r from-yellow-500 to-yellow-700 hover:from-yellow-600 hover:to-yellow-800 text-white"
                >
                  Soumettre une autre proposition
                </Button>
              </motion.div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-yellow-400">Informations sur le concours</h2>
                    
                    <motion.div variants={itemVariants}>
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white">Nom du concours</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Trophy className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                <Input 
                                  placeholder="Ex: Miss Afrique 2025" 
                                  className="pl-10 bg-black/50 border-white/20 text-white h-12" 
                                  {...field} 
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white">Description du concours</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Décrivez le concours, son objectif, ses règles..."
                                {...field}
                                className="min-h-[120px] bg-black/50 border-white/20 text-white"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <motion.div variants={itemVariants}>
                        <FormField
                          control={form.control}
                          name="start_date"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white">Date de début</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Calendar className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                  <Input 
                                    type="datetime-local" 
                                    className="pl-10 bg-black/50 border-white/20 text-white h-12" 
                                    {...field} 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>

                      <motion.div variants={itemVariants}>
                        <FormField
                          control={form.control}
                          name="end_date"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white">Date de fin</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Calendar className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                  <Input 
                                    type="datetime-local" 
                                    className="pl-10 bg-black/50 border-white/20 text-white h-12" 
                                    {...field} 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <motion.div variants={itemVariants}>
                        <FormField
                          control={form.control}
                          name="cover"
                          render={({ field: { value, onChange, ...fieldProps } }) => (
                            <FormItem>
                              <FormLabel className="text-white">Image de couverture</FormLabel>
                              <FormControl>
                                <div className="space-y-4">
                                  <div className="relative">
                                    <Input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      id="cover-upload"
                                      onChange={(e) => {
                                        onChange(e.target.files);
                                        handleCoverChange(e);
                                      }}
                                      {...fieldProps}
                                    />
                                    <div className="border-2 border-dashed border-white/20 rounded-lg p-6 bg-black/50 flex flex-col items-center justify-center">
                                      {coverPreview ? (
                                        <img 
                                          src={coverPreview} 
                                          alt="Aperçu de couverture" 
                                          className="w-full h-40 object-cover rounded-lg" 
                                        />
                                      ) : (
                                        <>
                                          <Image className="h-8 w-8 text-gray-400 mb-2" />
                                          <label
                                            htmlFor="cover-upload"
                                            className="cursor-pointer text-center"
                                          >
                                            <span className="text-sm text-gray-400 block">Cliquez pour sélectionner une image</span>
                                            <span className="text-xs text-gray-500 mt-1 block">JPG, PNG, WEBP (max 5MB)</span>
                                          </label>
                                        </>
                                      )}
                                      {coverPreview && (
                                        <label
                                          htmlFor="cover-upload"
                                          className="mt-2 text-sm text-yellow-400 cursor-pointer hover:text-yellow-300"
                                        >
                                          Changer l'image
                                        </label>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>

                      <motion.div variants={itemVariants}>
                        <FormField
                          control={form.control}
                          name="logo"
                          render={({ field: { value, onChange, ...fieldProps } }) => (
                            <FormItem>
                              <FormLabel className="text-white">Logo du concours</FormLabel>
                              <FormControl>
                                <div className="space-y-4">
                                  <div className="relative">
                                    <Input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      id="logo-upload"
                                      onChange={(e) => {
                                        onChange(e.target.files);
                                        handleLogoChange(e);
                                      }}
                                      {...fieldProps}
                                    />
                                    <div className="border-2 border-dashed border-white/20 rounded-lg p-6 bg-black/50 flex flex-col items-center justify-center">
                                      {logoPreview ? (
                                        <div className="w-32 h-32 mx-auto">
                                          <img 
                                            src={logoPreview} 
                                            alt="Aperçu de logo" 
                                            className="w-full h-full object-contain" 
                                          />
                                        </div>
                                      ) : (
                                        <>
                                          <Image className="h-8 w-8 text-gray-400 mb-2" />
                                          <label
                                            htmlFor="logo-upload"
                                            className="cursor-pointer text-center"
                                          >
                                            <span className="text-sm text-gray-400 block">Cliquez pour sélectionner un logo</span>
                                            <span className="text-xs text-gray-500 mt-1 block">JPG, PNG, WEBP (max 5MB)</span>
                                          </label>
                                        </>
                                      )}
                                      {logoPreview && (
                                        <label
                                          htmlFor="logo-upload"
                                          className="mt-2 text-sm text-yellow-400 cursor-pointer hover:text-yellow-300"
                                        >
                                          Changer le logo
                                        </label>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/10 space-y-6">
                    <h2 className="text-xl font-semibold text-yellow-400">Informations de contact</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <motion.div variants={itemVariants}>
                        <FormField
                          control={form.control}
                          name="contact_email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white">Email de contact</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                  <Input 
                                    type="email" 
                                    placeholder="votremail@exemple.com" 
                                    className="pl-10 bg-black/50 border-white/20 text-white h-12" 
                                    {...field} 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>
                      
                      <motion.div variants={itemVariants}>
                        <FormField
                          control={form.control}
                          name="contact_phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white">Téléphone de contact</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                  <Input 
                                    placeholder="+237 6XXXXXXXX" 
                                    className="pl-10 bg-black/50 border-white/20 text-white h-12" 
                                    {...field} 
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <motion.div variants={itemVariants}>
                        <FormField
                          control={form.control}
                          name="submitter_name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white">Votre nom</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="Nom complet" 
                                  className="bg-black/50 border-white/20 text-white h-12" 
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>
                      
                      <motion.div variants={itemVariants}>
                        <FormField
                          control={form.control}
                          name="submitter_organization"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white">Organisation (optionnel)</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="Nom de votre organisation" 
                                  className="bg-black/50 border-white/20 text-white h-12" 
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>
                    </div>
                  </div>

                  <motion.div variants={itemVariants}>
                    <FormField
                      control={form.control}
                      name="acceptTerms"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-white">
                              J'accepte les conditions de service et la politique de confidentialité
                            </FormLabel>
                            <FormDescription className="text-gray-400 text-xs">
                              En soumettant ce formulaire, vous acceptez que vos informations soient utilisées pour traiter votre proposition de concours.
                            </FormDescription>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </motion.div>

                  <motion.div variants={itemVariants} className="pt-4">
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-gradient-to-r from-yellow-500 to-yellow-700 hover:from-yellow-600 hover:to-yellow-800 text-white h-12 text-lg"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Soumission en cours...
                        </>
                      ) : (
                        'Soumettre ma proposition de concours'
                      )}
                    </Button>
                  </motion.div>
                </form>
              </Form>
            )}
          </motion.div>
        </div>
      </div>
      
      <div className="mt-16">
        <div className="bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 p-6">
          <TopFiveGlobal />
        </div>
      </div>
      <PublicFooter />
    </>
  );
};

export default ContestSubmission;
